export interface DebugLogEntry {
  id: string;
  timestamp: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'blockchain';
  category: string;
  message: string;
  data?: any;
}

class DebugLoggerService {
  private logs: DebugLogEntry[] = [];
  private maxLogs = 100;
  private listeners: ((logs: DebugLogEntry[]) => void)[] = [];

  private formatTimestamp(): string {
    const now = new Date();
    return now.toLocaleTimeString('zh-TW', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  private addLog(type: DebugLogEntry['type'], category: string, message: string, data?: any) {
    const logEntry: DebugLogEntry = {
      id: Math.random().toString(36).substr(2, 9),
      timestamp: this.formatTimestamp(),
      type,
      category,
      message,
      data
    };

    this.logs.unshift(logEntry);
    
    // 限制log數量
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    // 通知所有監聽器
    this.listeners.forEach(listener => listener([...this.logs]));

    const consoleMessage = `[${logEntry.timestamp}] [${category}] ${message}`;
    if (type === 'error') {
      console.error(consoleMessage, data);
    } else if (import.meta.env.VITE_SUPPRESS_LOGS !== 'true') {
      switch (type) {
        case 'warning':
          console.warn(consoleMessage, data);
          break;
        case 'success':
          console.log(`%c${consoleMessage}`, 'color: green', data);
          break;
        case 'blockchain':
          console.log(`%c${consoleMessage}`, 'color: #00d4aa', data);
          break;
        default:
          console.log(consoleMessage, data);
      }
    }
  }

  // 公開方法
  info(category: string, message: string, data?: any) {
    this.addLog('info', category, message, data);
  }

  success(category: string, message: string, data?: any) {
    this.addLog('success', category, message, data);
  }

  warning(category: string, message: string, data?: any) {
    this.addLog('warning', category, message, data);
  }

  error(category: string, message: string, data?: any) {
    this.addLog('error', category, message, data);
  }

  blockchain(category: string, message: string, data?: any) {
    this.addLog('blockchain', category, message, data);
  }

  // 監聽器管理
  subscribe(listener: (logs: DebugLogEntry[]) => void): () => void {
    this.listeners.push(listener);
    // 立即發送當前logs
    listener([...this.logs]);
    
    // 返回取消訂閱函數
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // 獲取所有logs
  getLogs(): DebugLogEntry[] {
    return [...this.logs];
  }

  // 清除所有logs
  clear() {
    this.logs = [];
    this.listeners.forEach(listener => listener([]));
  }
}

// 創建單例實例
export const debugLogger = new DebugLoggerService();

// 便捷的全域函數
export const debug = {
  info: (category: string, message: string, data?: any) => debugLogger.info(category, message, data),
  success: (category: string, message: string, data?: any) => debugLogger.success(category, message, data),
  warning: (category: string, message: string, data?: any) => debugLogger.warning(category, message, data),
  error: (category: string, message: string, data?: any) => debugLogger.error(category, message, data),
  blockchain: (category: string, message: string, data?: any) => debugLogger.blockchain(category, message, data),
};